import React, { useState } from "react";
import { Link } from "react-router-dom";
import VouchersAPI from "../../../api/v3/vouchers-api-v3";

const BASE = "/app/sales/bagspa";
const SAMPLE_CSV = [
  "code,discount_type,discount_value,applies_to_scope,product_category_codes,order_category_codes,active,expiration_date,max_redemption_per_user,promotional_text",
  "RFV001,fixed_amount,500000,product_categories,\"BG, WL\",,true,2026-05-31 23:59:59,1,Bag & wallet voucher",
  "RFV002,percentage,10,all_items,,,true,2026-05-31 23:59:59,1,All item voucher",
].join("\n");
const SAMPLE_CSV_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`;

export default function VoucherImportPage() {
  const [file, setFile] = useState(null);
  const [defaults, setDefaults] = useState({
    discount_type: "fixed_amount",
    discount_value: "",
    maximum_discount_value: "",
    minimum_transaction_value: "",
    max_redemption_per_user: 1,
    active: true,
    promotional_text: "",
    expiration_date: "",
    applies_to_scope: "all_items",
    product_category_slugs: "",
    order_category_slugs: "",
  });

  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState("");
  const [err, setErr]     = useState("");

  const updateDefault = (k, v) => setDefaults((s) => ({ ...s, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!file) { setErr("Please choose a CSV file"); return; }

    setBusy(true);
    setErr("");
    setMsg("");

    try {
      // POST /api/v3/vouchers/import (multipart/form-data)
      const resp = await VouchersAPI.importCSV(file, defaults);
      // Expecting something like { created, updated, errors: [...] }
      const created   = resp?.created ?? 0;
      const updated   = resp?.updated ?? 0;
      const errorsArr = Array.isArray(resp?.errors) ? resp.errors : [];
      setMsg(`Imported successfully → created=${created}, updated=${updated}${errorsArr.length ? " (see warnings below)" : ""}`);
      if (errorsArr.length) setErr(errorsArr.join("\n"));
    } catch (e2) {
      setErr(e2.message || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Import Vouchers (CSV)</h2>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary" to={`${BASE}/vouchers`}>Back</Link>
          <a
            className="btn btn-outline-primary"
            href={SAMPLE_CSV_URL}
            download="voucher-import-template.csv"
          >
            Download Template
          </a>
          <a
            className="btn btn-primary"
            href={VouchersAPI.exportURL({})}
            target="_blank"
            rel="noopener noreferrer"
          >
            Export CSV
          </a>
        </div>
      </div>

      {msg && <div className="alert alert-success" style={{ whiteSpace: "pre-wrap" }}>{msg}</div>}
      {err && <div className="alert alert-warning" style={{ whiteSpace: "pre-wrap" }}>{err}</div>}

      <form className="card shadow-sm" onSubmit={onSubmit}>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">CSV File</label>
            <input
              type="file"
              accept=".csv"
              className="form-control"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <div className="form-text">
              File must include a <code>code</code> column (or a column containing the word “voucher”).
              Optional columns: <code>discount_type</code>, <code>discount_value</code>, <code>maximum_discount_value</code>, <code>minimum_transaction_value</code>, <code>max_redemption_per_user</code>, <code>active</code>, <code>expiration_date</code>, <code>applies_to_scope</code>, <code>product_category_codes</code>, <code>order_category_codes</code>, <code>promotional_text</code>.
            </div>
          </div>

          <hr />

          <h5>Defaults (optional)</h5>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Discount Type</label>
              <select
                className="form-select"
                value={defaults.discount_type}
                onChange={(e) => updateDefault("discount_type", e.target.value)}
              >
                <option value="fixed_amount">fixed_amount</option>
                <option value="percentage">percentage</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Discount Value</label>
              <input
                className="form-control"
                value={defaults.discount_value}
                onChange={(e) => updateDefault("discount_value", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Max Discount</label>
              <input
                className="form-control"
                value={defaults.maximum_discount_value}
                onChange={(e) => updateDefault("maximum_discount_value", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Min Transaction</label>
              <input
                className="form-control"
                value={defaults.minimum_transaction_value}
                onChange={(e) => updateDefault("minimum_transaction_value", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Applies To</label>
              <select
                className="form-select"
                value={defaults.applies_to_scope}
                onChange={(e) => updateDefault("applies_to_scope", e.target.value)}
              >
                <option value="all_items">all_items</option>
                <option value="product_categories">product_categories</option>
                <option value="order_categories">order_categories</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Product Category Codes</label>
              <input
                className="form-control"
                placeholder="BG, WL"
                value={defaults.product_category_slugs}
                onChange={(e) => updateDefault("product_category_slugs", e.target.value)}
              />
              <div className="form-text">
                Use category codes from Taxonomy, e.g. <code>BG</code>, <code>WL</code>.
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Order Category/Treatment Codes</label>
              <input
                className="form-control"
                placeholder="FULL_TREATMENT-BAG-LEATHER-S, CLEANING-SHOES-M"
                value={defaults.order_category_slugs}
                onChange={(e) => updateDefault("order_category_slugs", e.target.value)}
              />
              <div className="form-text">
                Used when applies_to_scope = <b>order_categories</b>.
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Max Redemption / User</label>
              <input
                className="form-control"
                type="number"
                value={defaults.max_redemption_per_user}
                onChange={(e) => updateDefault("max_redemption_per_user", e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Active?</label>
              <div className="form-check">
                <input
                  id="imp_active"
                  type="checkbox"
                  className="form-check-input"
                  checked={!!defaults.active}
                  onChange={(e) => updateDefault("active", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="imp_active">
                  Active
                </label>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Expiration</label>
              <input
                type="datetime-local"
                className="form-control"
                value={defaults.expiration_date}
                onChange={(e) => updateDefault("expiration_date", e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Promotional Text</label>
              <input
                className="form-control"
                value={defaults.promotional_text}
                onChange={(e) => updateDefault("promotional_text", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card-footer d-flex justify-content-end gap-2">
          <Link className="btn btn-outline-secondary" to={`${BASE}/vouchers`}>Cancel</Link>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Importing..." : "Import"}
          </button>
        </div>
      </form>
    </div>
  );
}
